import { FunctionComponent } from "react";
import { FormikValues } from "formik";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import styles from "./stepped-modal.module.scss";
import type {
    CustomSteppedModalFactory,
    IProps,
    ISteppedModalFormikProps,
    ISteppedModalHeaderProps,
} from "./stepped-modal.types";
import { componentWrapper } from "../../utilities";
import { Modal } from "../modal";
import { Button } from "../button";
import { SteppedModalFooter } from "./stepped-modal-footer";
import {
    clampStepIndex,
    getFooter,
    getSteps,
    SteppedFlowFormikForm,
    SteppedFlowFormikStep,
    SteppedFlowStep,
    SteppedFlowStepper,
    useSteppedFlowFormik,
} from "../stepped-flow";

/**
 * Shared modal header for both the base and Formik SteppedModal variants: the step indicator and
 * the close button. Clicking a previous step navigates back; future steps are not clickable.
 */
const SteppedModalHeader: FunctionComponent<ISteppedModalHeaderProps> = ({
    active,
    onStepChange,
    steps,
    closeButtonDisabled = false,
}) => (
    <Modal.Header className={styles["header"]}>
        <SteppedFlowStepper active={active} onStepChange={onStepChange} steps={steps} />
        <Modal.CloseButton disabled={closeButtonDisabled} />
    </Modal.Header>
);

/**
 * Modal with multi-step navigation. The header shows a step indicator (numbered pills); only the
 * active step's content is rendered in the body. Fully controlled via `active`/`onStepChange`.
 * Clicking a previous step navigates back; future steps are not clickable.
 */
const SteppedModal = componentWrapper<CustomSteppedModalFactory, IProps>(
    "SteppedModal",
    ({ active, onStepChange, children, closeButtonDisabled, ...otherProps }, ref) => {
        const steps = getSteps(children);
        const footer = getFooter(children, SteppedModalFooter);
        const clampedActive = clampStepIndex(active, steps.length);

        return (
            <Modal.Root ref={ref} size="xl" {...otherProps}>
                <Modal.Overlay />
                <Modal.Content>
                    <SteppedModalHeader
                        active={clampedActive}
                        onStepChange={onStepChange}
                        steps={steps}
                        closeButtonDisabled={closeButtonDisabled}
                    />
                    <Modal.Body>{steps[clampedActive]}</Modal.Body>
                    {footer}
                </Modal.Content>
            </Modal.Root>
        );
    }
);

/**
 * Formik-aware SteppedModal. It owns the form: pass the Formik config (initialValues, onSubmit, …) as
 * props and the SteppedModal.Formik.Step children as content, each with its own `validation` schema.
 * The whole-form validation schema is derived by concatenating every step's schema, so there is no
 * separate `validationSchema` prop to drift out of sync with the steps. A single Formik wraps the
 * active step's content inside the modal body, so form state persists while navigating between steps
 * and is reset when the modal closes (the body unmounts). The footer (Back/Next/submit) is built in
 * and driven by the form's validity/submitting state, with `submitButtonDisabled` available for
 * validity Formik cannot see. The modal cannot be dismissed while the form is submitting.
 */
const SteppedModalFormik = <Values extends FormikValues = FormikValues>({
    active,
    onStepChange,
    children,
    submitButtonText = "Save",
    hideSubmitButton = false,
    submitButtonDisabled = false,
    initialValues,
    onSubmit,
    enableReinitialize,
    validateOnMount,
    ...modalProps
}: ISteppedModalFormikProps<Values>) => {
    const {
        steps,
        active: clampedActive,
        isFinalStep,
        formik,
        isSubmitting,
        isValid,
        isActiveStepValid,
        isValidationReady,
        formState,
    } = useSteppedFlowFormik<Values>({ active, children, stepName: "SteppedModal.Formik.Step" });
    const nextButtonText =
        steps[clampedActive]?.props.nextButtonText ?? `Next: ${steps[clampedActive + 1]?.props.label ?? ""}`;

    return (
        <Modal.Root size="xl" closeOnClickOutside={!isSubmitting} closeOnEscape={!isSubmitting} {...modalProps}>
            <Modal.Overlay />
            <Modal.Content>
                <SteppedModalHeader
                    active={clampedActive}
                    onStepChange={onStepChange}
                    steps={steps}
                    closeButtonDisabled={isSubmitting}
                />
                <Modal.Body>
                    <SteppedFlowFormikForm<Values>
                        {...formState}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        enableReinitialize={enableReinitialize}
                        validateOnMount={validateOnMount}
                    />
                </Modal.Body>
                <SteppedModalFooter>
                    {active > 0 && (
                        <Button
                            variant="default"
                            icon={faChevronLeft}
                            onClick={() => onStepChange(active - 1)}
                            disabled={isSubmitting}
                            data-testid="stepped-modal-back-button"
                        >
                            Back
                        </Button>
                    )}
                    {/* With zero recognised steps, `isFinalStep` (`0 === -1`) is false, so without this
                        guard an empty-labelled Next would render for a modal with no
                        SteppedModal.Formik.Step children (e.g. base SteppedModal.Step children passed
                        here by mistake — getFormikSteps filters them all out). */}
                    {!isFinalStep && steps.length > 0 && (
                        <Button
                            variant="filled"
                            onClick={() => onStepChange(active + 1)}
                            disabled={formik === null || !isActiveStepValid || isSubmitting}
                            data-testid="stepped-modal-next-button"
                            rightIcon={faChevronRight}
                        >
                            {nextButtonText}
                        </Button>
                    )}
                    {isFinalStep && !hideSubmitButton && (
                        <Modal.Footer.Actions.Done
                            text={submitButtonText}
                            onClick={() => formik?.submitForm()}
                            loading={isSubmitting}
                            disabled={
                                formik === null ||
                                !isValid ||
                                isSubmitting ||
                                submitButtonDisabled ||
                                !isValidationReady
                            }
                        />
                    )}
                </SteppedModalFooter>
            </Modal.Content>
        </Modal.Root>
    );
};

SteppedModal.Step = SteppedFlowStep;
SteppedModal.Footer = SteppedModalFooter;
SteppedModal.Formik = SteppedModalFormik;

// Attached after declaration rather than inline because SteppedModalFormik is a generic function
// component, so it cannot carry statics in its own type annotation.
SteppedModalFormik.Step = SteppedFlowFormikStep;

export { SteppedModal, SteppedModalFormik };
